<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import DismissButton from 'frontend/common/dismiss-button.vue'
import FileThumbnail from 'frontend/common/files/components/file-thumbnail.vue'
import type { IFileResponseEntity } from 'frontend/common/files/types/i-file-response-entity'
import Modal from 'frontend/common/modal.vue'
import { useKeyboardShortcuts } from 'frontend/keyboard/composables/useKeyboardShortcuts'
import { KeyboardSpecialKeyTypes } from 'frontend/keyboard/enum/KeyboardSpecialKeyTypes'
import { computed, ref } from 'vue'

interface Props {
  title: string
  currentFileUniqueId: string
  files: IFileResponseEntity[]
}
const props = defineProps<Props>()

interface Emits {
  (e: 'closed'): void
  (e: 'update:currentFileUniqueId', uniqueId: string): void
}
const emit = defineEmits<Emits>()

const currentFileUniqueIdModel = useVModel(props, 'currentFileUniqueId', emit)

useKeyboardShortcuts([
  [
    [[KeyboardSpecialKeyTypes.Left]],
    () => {
      goToFileByIndex(-1)
    },
  ],
  [
    [[KeyboardSpecialKeyTypes.Right]],
    () => {
      goToFileByIndex(1)
    },
  ],
])

const thumbnailsWrapperRef = ref<HTMLElement | null>(null)

const currentFile = computed(() => {
  return props.files.find(file => file.uniqueId === currentFileUniqueIdModel.value)
})

function goToFileByIndex(index: number): void {
  const currentIndex = props.files.findIndex(
    file => file.uniqueId === currentFileUniqueIdModel.value,
  )
  const anotherFile = props.files[currentIndex + index]
  if (anotherFile) {
    currentFileUniqueIdModel.value = anotherFile.uniqueId
  }
  const targetImageLeftStart = (currentIndex + index) * (80 + 16) - 16
  const targetImageLeftEnd = targetImageLeftStart + 80
  const currentScrollX = thumbnailsWrapperRef.value?.scrollLeft || 0
  const currentScrollClientWidth = thumbnailsWrapperRef.value?.clientWidth || 0

  const isWholeImageVisible =
    targetImageLeftStart >= currentScrollX &&
    targetImageLeftEnd <= currentScrollX + currentScrollClientWidth
  if (!isWholeImageVisible) {
    thumbnailsWrapperRef.value?.scrollTo({
      left: targetImageLeftStart,
    })
  }
}

function isImage(file): boolean {
  return file.contentType?.length
    ? file.contentType.startsWith('image/')
    : ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(file.url?.split('.')?.pop() || 'unknown')
}

function canGoByIndex(index: number): boolean {
  const currentIndex = props.files.findIndex(
    file => file.uniqueId === currentFileUniqueIdModel.value,
  )
  return !!props.files[currentIndex + index]
}
</script>

<template lang="pug">
modal.files-preview-modal(
  @closed="currentFileUniqueIdModel = null"
  scrollableContent
  showContentInGrid
  size="xl"
)
  template(#header)
    slot(v-if="$slots.header" name="header")
    div(v-else) File preview
  template(#default)
    .files-preview-modal-content
      .current-file-wrapper
        .arrow.left.mi-outlined.mi-chevron-left(
          :class="{ 'is-enabled': canGoByIndex(-1) }"
          @click="goToFileByIndex(-1)"
        )

        .current-file-image(
          v-if="isImage(currentFile) && currentFile.url"
          :style="{ backgroundImage: `url(${currentFile.url})` }"
        )
        .current-file-image.d-flex.align-items-center.justify-content-center(
          v-else-if="!isImage(currentFile) || !currentFile.url"
        )
          .d-flex.align-items-center.gap-2.flex-column
            i.bi.bi-file-earmark.fs-1
            a.d-block(v-if="currentFile.url" :href="currentFile.url" target="_blank") {{ currentFile.fileName }}
            span.d-block.text-muted(v-else) {{ currentFile.fileName }}

        .arrow.right.mi-outlined.mi-chevron-right(
          :class="{ 'is-enabled': canGoByIndex(1) }"
          @click="goToFileByIndex(1)"
        )

      .thumbnails(ref="thumbnailsWrapperRef")
        file-thumbnail(
          v-for="file in files"
          :file="file"
          :is-current="file.uniqueId === currentFileUniqueIdModel"
          :key="file.uniqueId"
          :preferred-variant-size="[80, 80]"
          @imageClick="currentFileUniqueIdModel = file.uniqueId"
          hide-delete-icon
        )
  template(#footer="modal")
    dismiss-button(@click="modal.close()")
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'frontend_stylesheets/theme-variables' as themeVariables;

.files-preview-modal-content {
  display: grid;
  grid-template-rows: 1fr auto;
  gap: 16px;
  user-select: none;
}

.files-preview-modal {
  & :deep(.modal-body) {
    display: flex;
  }
}
.thumbnails {
  display: grid;
  grid-auto-columns: 80px;
  grid-auto-flow: column;
  gap: 16px;
  overflow-x: auto;
}

.current-file-wrapper {
  display: grid;
  grid-template-columns: auto 1fr auto;
  overflow: hidden;
}

.current-file-image {
  width: 100%;
  height: 100%;
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
}

.arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: map.get(themeVariables.$colors, outlineVariant);

  &.is-enabled {
    color: map.get(themeVariables.$colors, primary);
    cursor: pointer;
  }
}
</style>
